import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import {
  LayoutDashboard, ShoppingCart, Package, Users, Heart, FileImage, DollarSign,
  Truck, ClipboardList, Building2, Settings, BarChart3, ArrowRight, BookOpen,
  ChevronDown, ChevronRight, Search, Printer, Receipt, Shield, Brain, Stethoscope,
  AlertTriangle, TrendingUp, Upload, ArrowDownUp, HelpCircle, Sparkles, CircleDot,
  CheckCircle2, Clock, CreditCard, Banknote, Smartphone, UserCheck, Eye, Pill,
  Download
} from 'lucide-react';
import api from '../lib/api';
import { StartTourButton } from '../components/OnboardingTour';

const SECTIONS = [
  { id: 'getting-started', label: 'Getting Started', icon: BookOpen },
  { id: 'journey', label: 'Customer Journey', icon: ArrowRight },
  { id: 'pos', label: 'Point of Sale', icon: ShoppingCart },
  { id: 'inventory', label: 'Inventory', icon: Package },
  { id: 'clinical', label: 'Clinical & Rx', icon: Stethoscope },
  { id: 'procurement', label: 'Procurement', icon: Truck },
  { id: 'pricing', label: 'Pricing', icon: DollarSign },
  { id: 'reports', label: 'Reports', icon: BarChart3 },
  { id: 'admin', label: 'Administration', icon: Shield },
  { id: 'faq', label: 'FAQ', icon: HelpCircle },
];

function StepCard({ step, title, desc, children }) {
  return (
    <div className="flex gap-4 items-start">
      <div className="flex-shrink-0 w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm">{step}</div>
      <div className="flex-1 min-w-0">
        <h4 className="font-semibold text-sm">{title}</h4>
        <p className="text-sm text-muted-foreground mt-0.5">{desc}</p>
        {children}
      </div>
    </div>
  );
}

function ScreenshotBox({ src, alt, caption }) {
  return (
    <div className="my-4 rounded-lg border overflow-hidden bg-muted/30">
      <img src={src} alt={alt} className="w-full h-auto" loading="lazy" />
      {caption && <p className="text-xs text-muted-foreground px-3 py-2 bg-muted/50 border-t">{caption}</p>}
    </div>
  );
}

function ExampleBox({ title, children }) {
  return (
    <div className="my-3 p-4 rounded-lg border-2 border-dashed border-primary/20 bg-primary/[0.02]">
      <p className="text-xs font-semibold text-primary uppercase tracking-wider mb-2">{title}</p>
      {children}
    </div>
  );
}

function JourneyStep({ icon: Icon, title, desc, color, isLast }) {
  return (
    <div className="flex items-start gap-3 relative">
      <div className="flex flex-col items-center">
        <div className={`w-10 h-10 rounded-full ${color} flex items-center justify-center text-white`}>
          <Icon className="h-5 w-5" />
        </div>
        {!isLast && <div className="w-0.5 h-12 bg-border mt-1" />}
      </div>
      <div className="pt-1.5 pb-6">
        <p className="font-semibold text-sm">{title}</p>
        <p className="text-sm text-muted-foreground">{desc}</p>
      </div>
    </div>
  );
}

function CollapsibleSection({ id, title, icon: Icon, defaultOpen, children }) {
  const [open, setOpen] = useState(defaultOpen || false);
  return (
    <Card id={id} className="scroll-mt-20" data-testid={`help-section-${id}`}>
      <button className="w-full text-left" onClick={() => setOpen(!open)}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2.5">
              <Icon className="h-5 w-5 text-primary" /> {title}
            </CardTitle>
            {open ? <ChevronDown className="h-5 w-5 text-muted-foreground" /> : <ChevronRight className="h-5 w-5 text-muted-foreground" />}
          </div>
        </CardHeader>
      </button>
      {open && <CardContent className="pt-0">{children}</CardContent>}
    </Card>
  );
}

export default function HelpPage() {
  const navigate = useNavigate();
  const [downloading, setDownloading] = useState(false);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const res = await api.get('/help/download-pdf', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'DutiLyne_Help_Guide.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Download failed:', e);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto" data-testid="help-page">
      {/* Header */}
      <div className="text-center py-6">
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Help & Tutorials
        </h1>
        <p className="text-muted-foreground mt-2 max-w-xl mx-auto">
          Everything you need to know to operate DutiLyne. Step-by-step guides with real examples.
        </p>
        <div className="mt-4 flex flex-wrap gap-2 justify-center">
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={handleDownloadPDF}
            disabled={downloading}
            data-testid="download-help-pdf-btn"
          >
            <Download className="h-4 w-4" />
            {downloading ? 'Generating PDF...' : 'Download as PDF'}
          </Button>
          <StartTourButton />
        </div>
      </div>

      {/* Quick Navigation */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap gap-2 justify-center">
            {SECTIONS.map(s => (
              <Button key={s.id} variant="outline" size="sm" className="gap-1.5 text-xs" onClick={() => scrollTo(s.id)}>
                <s.icon className="h-3 w-3" /> {s.label}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* =================== GETTING STARTED =================== */}
      <CollapsibleSection id="getting-started" title="Getting Started" icon={BookOpen} defaultOpen={true}>
        <div className="space-y-6">
          <p className="text-sm text-muted-foreground">Follow these steps to start using DutiLyne for the first time.</p>

          <ScreenshotBox src="/help/login.png" alt="Login page" caption="The DutiLyne login screen. Enter your email and password provided by your administrator." />

          <div className="space-y-5">
            <StepCard step="1" title="Sign In" desc="Enter the email and password your admin gave you. Click 'Sign In'. You'll be taken to the Dashboard." />
            <StepCard step="2" title="Explore the Dashboard" desc="The Dashboard shows a quick overview: total products, today's revenue, low stock alerts, and recent sales.">
              <ScreenshotBox src="/help/dashboard.png" alt="Dashboard" caption="Your command center. Key numbers are shown at a glance: Products, Revenue, Low Stock, Expiring Soon." />
            </StepCard>
            <StepCard step="3" title="Navigate Using the Sidebar" desc="The left sidebar organizes everything into sections: Sales, Inventory, Clinical, AI Tools, Procurement, Finance, Reports, and Administration." />
            <StepCard step="4" title="Understand Your Role" desc="Your role determines what you can see and do:">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                {[
                  { role: 'Super Admin', desc: 'Full access to everything. Can manage branches, users, and settings.', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
                  { role: 'Pharmacist', desc: 'POS, inventory, patients, prescriptions, and clinical tools.', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
                  { role: 'Finance', desc: 'Read-only access across all branches. Financial reports and billing.', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
                  { role: 'Cashier', desc: 'POS and sales history only. The simplest view.', color: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400' },
                ].map(r => (
                  <div key={r.role} className={`p-3 rounded-lg ${r.color}`}>
                    <p className="font-semibold text-sm">{r.role}</p>
                    <p className="text-xs mt-0.5 opacity-80">{r.desc}</p>
                  </div>
                ))}
              </div>
            </StepCard>
          </div>
        </div>
      </CollapsibleSection>

      {/* =================== CUSTOMER JOURNEY MAP =================== */}
      <CollapsibleSection id="journey" title="Customer Journey Map" icon={ArrowRight} defaultOpen={true}>
        <div className="space-y-6">
          <p className="text-sm text-muted-foreground">This shows a typical customer visit from walk-in to checkout. Follow this flow for every transaction.</p>

          {/* Visual Journey */}
          <Card className="bg-muted/30 border-dashed">
            <CardContent className="py-6">
              <h3 className="font-semibold text-center mb-6 text-base">Walk-in Customer Journey</h3>
              <div className="max-w-md mx-auto">
                <JourneyStep icon={UserCheck} title="1. Customer Arrives" desc="Customer walks in with a prescription or asks for over-the-counter medicine." color="bg-slate-500" />
                <JourneyStep icon={FileImage} title="2. Scan Prescription (if any)" desc="Go to Clinical > Rx Scanner. Upload or photograph the prescription. AI reads it automatically." color="bg-blue-500" />
                <JourneyStep icon={AlertTriangle} title="3. Drug Check" desc="System automatically checks for drug interactions if patient has history. Alerts appear if conflicts found." color="bg-amber-500" />
                <JourneyStep icon={Heart} title="4. Patient Record" desc="Patient is auto-saved from the prescription scan, or add them manually in Clinical > Patients." color="bg-pink-500" />
                <JourneyStep icon={ShoppingCart} title="5. Add to POS Cart" desc="Click 'Send to POS' from the scanner, or manually search and add products in the Point of Sale screen." color="bg-emerald-500" />
                <JourneyStep icon={CreditCard} title="6. Checkout" desc="Choose payment method (Cash, Mobile Money, Insurance). Apply discount if needed. Click 'Complete Sale'." color="bg-primary" />
                <JourneyStep icon={Printer} title="7. Print Receipt" desc="Print a thermal receipt or full invoice. Receipt is auto-generated with all sale details." color="bg-violet-500" />
                <JourneyStep icon={CheckCircle2} title="8. Done" desc="Stock is automatically reduced. Sale appears in history and financial reports instantly." color="bg-green-600" isLast />
              </div>
            </CardContent>
          </Card>

          {/* Second journey: Phone Order */}
          <Card className="bg-muted/30 border-dashed">
            <CardContent className="py-6">
              <h3 className="font-semibold text-center mb-6 text-base">Phone/Delivery Order Journey</h3>
              <div className="max-w-md mx-auto">
                <JourneyStep icon={Smartphone} title="1. Receive Call" desc="Customer calls or messages asking for specific medicines." color="bg-slate-500" />
                <JourneyStep icon={Search} title="2. Check Stock" desc="Search the product in Inventory > Products. Confirm availability and price." color="bg-blue-500" />
                <JourneyStep icon={ShoppingCart} title="3. Process at POS" desc="Add items to cart. Note customer name or order reference in the notes field." color="bg-emerald-500" />
                <JourneyStep icon={Banknote} title="4. Payment" desc="Accept Mobile Money payment or mark as 'Cash on Delivery'. Complete the sale." color="bg-primary" />
                <JourneyStep icon={Receipt} title="5. Record" desc="Sale recorded. Inventory updated. Print or share invoice digitally." color="bg-green-600" isLast />
              </div>
            </CardContent>
          </Card>
        </div>
      </CollapsibleSection>

      {/* =================== POINT OF SALE =================== */}
      <CollapsibleSection id="pos" title="Point of Sale (POS)" icon={ShoppingCart}>
        <div className="space-y-6">
          <p className="text-sm text-muted-foreground">The POS is your checkout counter. Here's how to make a sale.</p>

          <ScreenshotBox src="/help/pos.png" alt="POS screen" caption="The POS screen. Products on the left, cart on the right. Click a product card to add it to the cart." />

          <div className="space-y-5">
            <StepCard step="1" title="Search for a Product" desc="Use the search bar at the top. Type the medicine name, generic name, or scan a barcode. Products appear as cards below." />
            <StepCard step="2" title="Add to Cart" desc="Click a product card to add 1 unit. Click again to add more. The cart panel on the right shows your running total." />
            <StepCard step="3" title="Adjust Quantities" desc="In the cart, use the + and - buttons to change quantity. Click the trash icon to remove an item completely." />
            <StepCard step="4" title="Add Discount (Optional)" desc="You can apply a percentage or flat discount to the entire sale before checking out." />
            <StepCard step="5" title="Choose Payment Method" desc="Select Cash, Mobile Money, or Insurance Claim. For Mobile Money, enter the transaction reference." />
            <StepCard step="6" title="Complete Sale" desc="Click the 'Complete Sale' button. Stock is automatically reduced. A receipt is generated." />
          </div>

          <ExampleBox title="Example Transaction: Mrs. Ama Mensah">
            <div className="space-y-1 text-sm">
              <p>Mrs. Mensah comes in with a cough and headache. The cashier:</p>
              <div className="pl-4 space-y-1 text-muted-foreground">
                <p>1. Searches "Paracetamol" and clicks the card (GH¢ 4.90)</p>
                <p>2. Searches "Cough Syrup" and clicks (GH¢ 4.00)</p>
                <p>3. Cart shows: 2 items, Total = <strong className="text-foreground">GH¢ 8.90</strong></p>
                <p>4. Mrs. Mensah pays cash. Cashier clicks <strong className="text-foreground">"Complete Sale"</strong></p>
                <p>5. Receipt prints. Stock updated automatically.</p>
              </div>
            </div>
          </ExampleBox>

          <ExampleBox title="Example: Mobile Money Payment">
            <div className="space-y-1 text-sm">
              <p>Mr. Kofi orders Amoxicillin (GH¢ 22.00) and pays with MTN MoMo:</p>
              <div className="pl-4 space-y-1 text-muted-foreground">
                <p>1. Add Amoxicillin 250mg to cart</p>
                <p>2. Select <strong className="text-foreground">"Mobile Money"</strong> as payment method</p>
                <p>3. Enter MoMo reference: <strong className="text-foreground">TXN-12345678</strong></p>
                <p>4. Complete sale. Invoice shows payment method and reference.</p>
              </div>
            </div>
          </ExampleBox>

          <ExampleBox title="Example: Insurance Claim">
            <div className="space-y-1 text-sm">
              <p>Patient with NHIS card buys Metformin 500mg (GH¢ 3.50):</p>
              <div className="pl-4 space-y-1 text-muted-foreground">
                <p>1. Add product to cart</p>
                <p>2. Select <strong className="text-foreground">"Insurance Claim"</strong> as payment</p>
                <p>3. Enter NHIS member ID and provider name</p>
                <p>4. Complete sale. Appears in CRM &amp; Billing &gt; Insurance reports.</p>
              </div>
            </div>
          </ExampleBox>
        </div>
      </CollapsibleSection>

      {/* =================== INVENTORY =================== */}
      <CollapsibleSection id="inventory" title="Inventory Management" icon={Package}>
        <div className="space-y-6">
          <p className="text-sm text-muted-foreground">Keep track of all your medicines, their stock levels, and pricing.</p>

          <ScreenshotBox src="/help/inventory.png" alt="Inventory page" caption="The Products list. Shows name, category, price, stock quantity, and status (In Stock, Low Stock, Out of Stock)." />

          <h3 className="font-semibold text-base mt-4">Key Tasks</h3>
          <div className="space-y-5">
            <StepCard step="A" title="Add a New Product" desc="Click '+ Add Product' button (top right). Fill in the name, generic name, category, cost price, selling price, stock quantity, minimum stock level, and manufacturer. Click Save." />
            <StepCard step="B" title="Edit a Product" desc="Click the pencil icon on any product row. Update any field and save." />
            <StepCard step="C" title="Check Stock Status" desc="Filter by 'Stock Status' dropdown: In Stock, Low Stock, or Out of Stock. Low stock items are ones below their minimum level." />
            <StepCard step="D" title="Bulk Import Products" desc="Go to Inventory > Bulk Import. Upload a CSV file with columns: name, generic_name, category, unit_cost, unit_price, stock_quantity, min_stock_level, manufacturer." />
            <StepCard step="E" title="Adjust Stock" desc="Go to Inventory > Stock Adjust. Use this for corrections, damage write-offs, or receiving new stock outside of purchase orders." />
          </div>

          <ExampleBox title="Example: Adding a New Medicine">
            <div className="space-y-1 text-sm text-muted-foreground">
              <p>Your pharmacy just started stocking a new antibiotic:</p>
              <div className="pl-4 space-y-1">
                <p>Name: <strong className="text-foreground">Azithromycin 250mg</strong></p>
                <p>Generic: <strong className="text-foreground">Azithromycin</strong></p>
                <p>Category: <strong className="text-foreground">Antibiotics</strong></p>
                <p>Cost Price: <strong className="text-foreground">GH¢ 6.00</strong></p>
                <p>Selling Price: <strong className="text-foreground">GH¢ 10.00</strong></p>
                <p>Stock: <strong className="text-foreground">200 units</strong></p>
                <p>Min Stock Level: <strong className="text-foreground">30</strong> (alerts when below this)</p>
                <p>Requires Prescription: <strong className="text-foreground">Yes</strong></p>
              </div>
            </div>
          </ExampleBox>
        </div>
      </CollapsibleSection>

      {/* =================== CLINICAL =================== */}
      <CollapsibleSection id="clinical" title="Clinical & Prescriptions" icon={Stethoscope}>
        <div className="space-y-6">
          <p className="text-sm text-muted-foreground">Manage patients, scan prescriptions with AI, and check drug interactions.</p>

          <h3 className="font-semibold text-base">Patient Management</h3>
          <ScreenshotBox src="/help/patients.png" alt="Patients page" caption="The Patients list. Each patient can have multiple prescriptions linked to them." />
          <div className="space-y-4">
            <StepCard step="1" title="Add a Patient" desc="Click '+ Add Patient'. Enter their name, phone, email, date of birth, and any known allergies. This helps track their prescription history." />
            <StepCard step="2" title="View Patient Prescriptions" desc="Click any patient row to see all their past prescriptions and associated sales." />
          </div>

          <Separator className="my-6" />

          <h3 className="font-semibold text-base">AI Prescription Scanner</h3>
          <div className="space-y-4">
            <StepCard step="1" title="Go to Rx Scanner" desc="Navigate to Clinical > Rx Scanner." />
            <StepCard step="2" title="Upload Prescription" desc="Click the upload area or drag a photo/image of the prescription. AI will read the handwriting and extract medicine names, dosages, and quantities." />
            <StepCard step="3" title="Review Results" desc="The AI shows extracted medicines in a table. Review for accuracy. Edit if needed." />
            <StepCard step="4" title="Send to POS" desc="Click 'Send to POS' to auto-add all extracted medicines to the checkout cart." />
          </div>

          <Separator className="my-6" />

          <h3 className="font-semibold text-base">Drug Interaction Check</h3>
          <div className="space-y-4">
            <StepCard step="1" title="Navigate to Drug Check" desc="Go to Clinical > Drug Check." />
            <StepCard step="2" title="Enter Medicines" desc="Type two or more medicine names to check for known interactions." />
            <StepCard step="3" title="Review Alerts" desc="The system shows severity levels: Minor, Moderate, or Major. Major interactions are highlighted in red." />
          </div>
        </div>
      </CollapsibleSection>

      {/* =================== PROCUREMENT =================== */}
      <CollapsibleSection id="procurement" title="Procurement & Suppliers" icon={Truck}>
        <div className="space-y-6">
          <p className="text-sm text-muted-foreground">Manage your suppliers, create purchase orders, and track payments.</p>

          <ScreenshotBox src="/help/suppliers.png" alt="Suppliers page" caption="Your supplier directory. Add contact details, track orders, and record payments." />

          <div className="space-y-5">
            <StepCard step="1" title="Add a Supplier" desc="Go to Procurement > Suppliers. Click '+ Add Supplier'. Enter company name, contact person, phone, email, and address." />
            <StepCard step="2" title="Create a Purchase Order" desc="Go to Procurement > Purchases. Click '+ New PO'. Select a supplier, add products with quantities and costs. Save as Draft or submit as Ordered." />
            <StepCard step="3" title="Receive Goods" desc="When goods arrive, open the PO and mark it as 'Received'. Stock quantities update automatically." />
            <StepCard step="4" title="Record Payment to Supplier" desc="In the Supplier detail view, click 'Record Payment'. Enter amount, method (Cash, Bank Transfer, Mobile Money, or Cheque), and date." />
          </div>

          <ExampleBox title="Example: Ordering Paracetamol from MedSupply Corp">
            <div className="space-y-1 text-sm text-muted-foreground">
              <p>1. Go to Purchases &gt; Create New PO</p>
              <p>2. Supplier: <strong className="text-foreground">MedSupply Corp</strong></p>
              <p>3. Add: Paracetamol 500mg, Qty: <strong className="text-foreground">500</strong>, Unit Cost: <strong className="text-foreground">GH¢ 2.50</strong></p>
              <p>4. PO Total: <strong className="text-foreground">GH¢ 1,250.00</strong></p>
              <p>5. Save as Ordered. When delivered, mark as Received.</p>
              <p>6. Go to supplier profile &gt; Record Payment &gt; GH¢ 1,250 via Bank Transfer</p>
            </div>
          </ExampleBox>
        </div>
      </CollapsibleSection>

      {/* =================== PRICING =================== */}
      <CollapsibleSection id="pricing" title="Pricing Rules" icon={DollarSign}>
        <div className="space-y-6">
          <p className="text-sm text-muted-foreground">Set pricing formulas to automatically calculate selling prices from cost prices.</p>

          <ScreenshotBox src="/help/pricing.png" alt="Pricing page" caption="The Pricing Calculator. Choose your base price, method, and value to preview price changes before applying." />

          <h3 className="font-semibold text-base mt-4">Understanding Pricing Methods</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            {[
              { method: 'Markup %', formula: 'New Price = Base + (Base x %)', example: 'Cost GH¢ 10 + 30% = GH¢ 13.00' },
              { method: 'Margin %', formula: 'New Price = Base / (1 - %)', example: 'Cost GH¢ 10, 30% margin = GH¢ 14.29' },
              { method: 'Add To', formula: 'New Price = Base + Fixed Amount', example: 'Selling GH¢ 10 + GH¢ 5 = GH¢ 15.00' },
              { method: 'Subtract', formula: 'New Price = Base - Fixed Amount', example: 'Selling GH¢ 15 - GH¢ 2 = GH¢ 13.00' },
              { method: 'Multiply By', formula: 'New Price = Base x Multiplier', example: 'Cost GH¢ 10 x 1.5 = GH¢ 15.00' },
            ].map(m => (
              <div key={m.method} className="p-3 rounded-lg border bg-card">
                <p className="font-semibold text-sm">{m.method}</p>
                <p className="text-xs text-muted-foreground mt-1">{m.formula}</p>
                <p className="text-xs mt-1.5 font-mono text-primary">{m.example}</p>
              </div>
            ))}
          </div>

          <h3 className="font-semibold text-base mt-6">Base Price Options</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
            <div className="p-3 rounded-lg border bg-card">
              <p className="font-semibold text-sm">Cost Price</p>
              <p className="text-xs text-muted-foreground">Calculate from your purchase/wholesale cost. Best for setting initial selling prices.</p>
            </div>
            <div className="p-3 rounded-lg border bg-card">
              <p className="font-semibold text-sm">Selling Price</p>
              <p className="text-xs text-muted-foreground">Calculate from the current selling price. Best for across-the-board adjustments (e.g., add GH¢ 1 to all).</p>
            </div>
          </div>

          <ExampleBox title="Example: Setting 40% Markup on All Pain Relief Products">
            <div className="space-y-1 text-sm text-muted-foreground">
              <p>1. Go to Pricing tab &gt; Calculator</p>
              <p>2. Base Price: <strong className="text-foreground">Cost Price</strong></p>
              <p>3. Method: <strong className="text-foreground">Markup %</strong></p>
              <p>4. Value: <strong className="text-foreground">40</strong></p>
              <p>5. Scope: <strong className="text-foreground">By Category</strong> &gt; Select "Pain Relief"</p>
              <p>6. Click <strong className="text-foreground">"Calculate Prices"</strong></p>
              <p>7. Review the preview table. Edit any individual price if needed.</p>
              <p>8. Click <strong className="text-foreground">"Apply Prices"</strong> to save.</p>
            </div>
          </ExampleBox>
        </div>
      </CollapsibleSection>

      {/* =================== REPORTS =================== */}
      <CollapsibleSection id="reports" title="Reports & Analytics" icon={BarChart3}>
        <div className="space-y-6">
          <p className="text-sm text-muted-foreground">DutiLyne provides a wide range of reports. All can be exported to PDF or CSV.</p>

          <h3 className="font-semibold text-base">Available Reports</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            {[
              { name: 'Sales Summary', desc: 'Daily, weekly, monthly revenue breakdown with charts.', path: 'Reports & Analytics' },
              { name: 'Controlled Drug Report', desc: 'Tracks all narcotic/controlled substance sales. Required for regulatory compliance.', path: 'Reports & Analytics' },
              { name: 'Procurement Payments', desc: 'All payments made to suppliers with method breakdown.', path: 'Procurement > Purchases' },
              { name: 'Insurance Claims', desc: 'Individual and aggregate insurance billing reports.', path: 'CRM & Billing' },
              { name: 'Corporate Billing', desc: 'Corporate account billing summary with drill-down.', path: 'CRM & Billing' },
              { name: 'Branch KPIs', desc: 'Compare performance across all branches.', path: 'Administration > Branch KPIs' },
              { name: 'Price History', desc: 'Audit trail of all product price changes.', path: 'Inventory > Pricing' },
              { name: 'AI Demand Forecast', desc: 'AI-predicted demand for products to help with reordering.', path: 'AI Tools' },
            ].map(r => (
              <div key={r.name} className="p-3 rounded-lg border bg-card">
                <p className="font-semibold text-sm">{r.name}</p>
                <p className="text-xs text-muted-foreground">{r.desc}</p>
                <Badge variant="outline" className="text-[10px] mt-1.5">{r.path}</Badge>
              </div>
            ))}
          </div>

          <h3 className="font-semibold text-base mt-6">Exporting Reports</h3>
          <div className="space-y-4">
            <StepCard step="1" title="Navigate to the Report" desc="Open the relevant report page." />
            <StepCard step="2" title="Click Export Button" desc="Look for the 'Export PDF' or 'Export CSV' button (usually top right of the report)." />
            <StepCard step="3" title="File Downloads" desc="PDF opens in a new tab or downloads. CSV can be opened in Excel or Google Sheets." />
          </div>
        </div>
      </CollapsibleSection>

      {/* =================== ADMINISTRATION =================== */}
      <CollapsibleSection id="admin" title="Administration" icon={Shield}>
        <div className="space-y-6">
          <p className="text-sm text-muted-foreground">Admin tasks: managing users, branches, and system settings. Only Super Admins can access these.</p>

          <h3 className="font-semibold text-base">User Management</h3>
          <ScreenshotBox src="/help/users.png" alt="Users page" caption="The User Management page. Shows each user's role, branch assignment, and custom permissions." />
          <div className="space-y-4">
            <StepCard step="1" title="Add a New User" desc="Click '+ Add User'. Enter name, email, password, select a role, and assign to a branch." />
            <StepCard step="2" title="Edit User Role or Branch" desc="Click the pencil icon on any user. Change their role or reassign to a different branch." />
            <StepCard step="3" title="Custom Permissions" desc="In the edit dialog, go to the Permissions tab. Toggle individual permissions on/off to override the role defaults." />
          </div>

          <Separator className="my-6" />

          <h3 className="font-semibold text-base">Branch Management</h3>
          <ScreenshotBox src="/help/branches.png" alt="Branch management" caption="The Branch Management page. Each branch has isolated data (products, sales, patients). Super Admins can switch between branches." />
          <div className="space-y-4">
            <StepCard step="1" title="Switch Branch" desc="Use the 'All Branches' dropdown in the top-right header bar. Select a specific branch to filter all data to that location." />
            <StepCard step="2" title="Add a Branch" desc="Click '+ Add Branch'. Name it (e.g., 'Downtown Branch'). Each branch gets its own isolated inventory, sales, and patient data." />
            <StepCard step="3" title="View All Branches" desc="Select 'All Branches' to see combined data from every location." />
          </div>

          <Separator className="my-6" />

          <h3 className="font-semibold text-base">Settings & Branding</h3>
          <div className="space-y-4">
            <StepCard step="1" title="Change Business Name" desc="Go to Administration > Settings. Update your pharmacy name. It appears in the sidebar and all invoices." />
            <StepCard step="2" title="Upload Logo" desc="In Settings, upload your logo image. It replaces the default 'Rx' icon in the sidebar." />
            <StepCard step="3" title="Brand Colors" desc="Customize the primary color used throughout the app to match your pharmacy's branding." />
          </div>
        </div>
      </CollapsibleSection>

      {/* =================== FAQ =================== */}
      <CollapsibleSection id="faq" title="Frequently Asked Questions" icon={HelpCircle}>
        <div className="space-y-5">
          {[
            { q: 'What if the internet goes down?', a: 'The POS has an offline mode. Click "Prepare Offline" on the POS page before going offline. Sales will be saved locally and synced when internet returns. Other features (AI scanner, reports) require internet.' },
            { q: 'Can I undo a sale?', a: 'Completed sales cannot be undone to maintain accurate financial records. However, you can record a return or adjustment in Stock Adjust, and note the reason.' },
            { q: 'How do I reorder products that are running low?', a: 'Check the Dashboard for "Low Stock" items. Or go to AI Tools > Demand Forecast for AI-recommended reorder quantities. You can auto-generate purchase orders from the forecast.' },
            { q: 'How does the controlled drug tracking work?', a: 'Mark a product as "Controlled/Narcotic" when adding it. The system then requires a prescription for every sale, enforces pharmacist authorization, and logs all transactions in the Controlled Drug Report.' },
            { q: 'Can I use the system on my phone?', a: 'Yes! DutiLyne is fully responsive. Open it in your phone browser. The POS and most pages work well on mobile screens.' },
            { q: 'Who can see data from other branches?', a: 'Super Admins see all branches and can switch between them. Finance users have read-only access to all branches. Everyone else only sees their assigned branch data.' },
            { q: 'How do I export my data?', a: 'Most report pages have Export PDF and Export CSV buttons. Inventory can be exported from the Products page. Purchase orders can be exported from the Purchases tab.' },
            { q: 'What payment methods are supported?', a: 'Cash, Mobile Money (MTN, Vodafone, AirtelTigo), Insurance Claims, and Corporate Billing. Each records the reference number for reconciliation.' },
            { q: 'How do I change my password?', a: 'Contact your Super Admin. They can update your account from Administration > User Management.' },
            { q: 'What does the AI Demand Forecast do?', a: 'It analyzes your past sales data and predicts which products will need reordering soon. It suggests quantities and can auto-create purchase orders for critical items.' },
          ].map((faq) => (
            <div key={faq.q} className="p-4 rounded-lg border bg-card">
              <p className="font-semibold text-sm">{faq.q}</p>
              <p className="text-sm text-muted-foreground mt-1.5">{faq.a}</p>
            </div>
          ))}
        </div>
      </CollapsibleSection>

      {/* Quick Links Footer */}
      <Card>
        <CardContent className="py-5">
          <h3 className="font-semibold text-sm text-center mb-3">Quick Links</h3>
          <div className="flex flex-wrap gap-2 justify-center">
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => navigate('/pos')}><ShoppingCart className="h-3 w-3" /> Go to POS</Button>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => navigate('/inventory')}><Package className="h-3 w-3" /> Inventory</Button>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => navigate('/customers/records')}><Heart className="h-3 w-3" /> Customers</Button>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => navigate('/reports')}><BarChart3 className="h-3 w-3" /> Reports</Button>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => navigate('/settings')}><Settings className="h-3 w-3" /> Settings</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
